/**
 * Pure utility functions extracted from SnowDepthService.
 *
 * These functions perform date snapping, zoom-level mapping, tile coordinate
 * conversion, and date candidate generation with no side effects and no
 * dependency on DOM, network, or map state.
 */

/**
 * Find the closest available date string for a given Date value.
 *
 * Looks for an exact match in `availableDates` first. If none is found,
 * returns the latest date that is still <= the target date string. Falls
 * back to the last element of `availableDates` when the target is before
 * all available dates, and returns `''` when the list is empty.
 *
 * @param time - The reference date/time.
 * @param availableDates - Sorted array of date strings in YYYYMMDD format.
 * @returns The closest available date string, or `''` if none exist.
 */
export function dateFromTime(time: Date, availableDates: string[]): string {
  const y = time.getFullYear()
  const m = String(time.getMonth() + 1).padStart(2, '0')
  const d = String(time.getDate()).padStart(2, '0')
  const exact = `${y}${m}${d}`

  if (availableDates.includes(exact)) {
    return exact
  }

  for (let i = availableDates.length - 1; i >= 0; i--) {
    if (availableDates[i] <= exact) {
      return availableDates[i]
    }
  }

  return availableDates[availableDates.length - 1] || ''
}

/**
 * Generate date strings in YYYYMMDD format for the last `count` days,
 * starting from `referenceDate` (inclusive) and working backwards.
 *
 * @param count - Number of date candidates to generate (default 14).
 * @param referenceDate - The starting date (default: today).
 * @returns Array of date strings ordered newest-first.
 */
export function generateDateCandidates(count = 14, referenceDate: Date = new Date()): string[] {
  const dates: string[] = []
  for (let i = 0; i < count; i++) {
    const d = new Date(referenceDate)
    d.setDate(d.getDate() - i)
    const year = d.getFullYear()
    const month = String(d.getMonth() + 1).padStart(2, '0')
    const day = String(d.getDate()).padStart(2, '0')
    dates.push(`${year}${month}${day}`)
  }
  return dates
}

/**
 * Map a map zoom level to a GeoTIFF overview index.
 *
 * Higher zoom levels use finer-resolution overviews (lower index).
 *
 * | Zoom   | Overview | Approx resolution |
 * |--------|----------|-------------------|
 * | >= 10  | 0        | ~1 km (native)    |
 * |  8–9   | 1        | ~2 km             |
 * |  6–7   | 2        | ~4 km             |
 * |  < 6   | 3        | ~8 km             |
 *
 * @param z - Map zoom level.
 * @returns GeoTIFF overview index (0 = native resolution).
 */
export function zoomToOverviewIndex(z: number): number {
  if (z >= 10) {
    return 0
  }
  if (z >= 8) {
    return 1
  }
  if (z >= 6) {
    return 2
  }
  return 3
}

/**
 * Convert slippy-map tile coordinates (z/x/y) to geographic lat/lng bounds
 * using the Web Mercator (EPSG:3857) inverse projection.
 *
 * @param z - Zoom level.
 * @param x - Tile column index (0-based, left = west).
 * @param y - Tile row index (0-based, top = north).
 * @returns Object with `west`, `south`, `east`, `north` in decimal degrees.
 */
export function tileBoundsToLatLng(
  z: number,
  x: number,
  y: number
): { west: number; south: number; east: number; north: number } {
  const n = Math.pow(2, z)
  const west = (x / n) * 360 - 180
  const east = ((x + 1) / n) * 360 - 180
  const north = (Math.atan(Math.sinh(Math.PI * (1 - (2 * y) / n))) * 180) / Math.PI
  const south = (Math.atan(Math.sinh(Math.PI * (1 - (2 * (y + 1)) / n))) * 180) / Math.PI
  return { west, south, east, north }
}
